import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../features/auth/auth-context';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { toDisplayMessage } from '../../../lib/error-message';
import styles from './AdminPortal.module.css';

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

export function AdminProviderBanksPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const banks = useQuery({
    queryKey: ['admin', 'payment-provider', 'banks'],
    queryFn: () => mandatesApi.listProviderBanks(),
  });

  const refresh = useMutation({
    mutationFn: () => mandatesApi.refreshProviderBanks(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payment-provider', 'banks'] });
    },
  });

  const canRefresh = can(PERMISSIONS.PROVIDER_BANKS_REFRESH);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Provider banks</h1>
        <p className={styles.pageSubtitle}>
          Supported commercial banks cached from the active payment provider.
        </p>
      </header>

      {refresh.isError && <Alert tone="error">{toDisplayMessage(refresh.error)}</Alert>}
      {refresh.isSuccess && <Alert tone="success">Bank catalogue refreshed.</Alert>}

      <section className={styles.card} aria-labelledby="provider-banks-heading">
        <h2 className={styles.cardTitle} id="provider-banks-heading">
          Supported banks
        </h2>

        {canRefresh && (
          <div className={styles.actions}>
            <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>
              Refresh from provider
            </Button>
          </div>
        )}

        {banks.isPending && <PageSpinner label="Loading provider banks" />}
        {banks.isError && <Alert tone="error">{toDisplayMessage(banks.error)}</Alert>}

        {banks.isSuccess && banks.data.length === 0 && (
          <p className={styles.empty}>
            No provider banks are available yet. Someone with provider-bank refresh permission must
            synchronize the supported bank list.
          </p>
        )}

        {banks.isSuccess && banks.data.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Provider banks">
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Name</th>
                  <th scope="col">Provider</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last synchronized</th>
                </tr>
              </thead>
              <tbody>
                {banks.data.map((bank) => (
                  <tr key={`${bank.provider}:${bank.bankCode}`}>
                    <td>{bank.bankCode}</td>
                    <td>{bank.bankName}</td>
                    <td>{bank.provider}</td>
                    <td>
                      <StatusBadge status={bank.status} />
                    </td>
                    <td>{formatTimestamp(bank.lastSynchronizedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
